import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Modal, Table, Button, Space, message, Empty, Input, Spin } from "antd";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from "antd/es/table";
import type { SrmVendorBiddingVo } from "@/types/apis";
import { useVendorBiddingData } from "../hooks/useVendorBiddingData";

// ==================== 类型定义 ====================

interface QuotationModalProps {
  open: boolean;
  onClose: () => void;
  onSelectQuotation?: (quotationFiles: SrmVendorBiddingVo[]) => void;
}

export type QuotationFileRecord = SrmVendorBiddingVo;

/**
 * 报价单选择弹框
 * 支持分页、搜索和选择报价单
 */
const QuotationModal: React.FC<QuotationModalProps> = ({
  open,
  onClose,
  onSelectQuotation,
}) => {
  const { t } = useTranslation("supplierProfile");
  const {
    data: quotationList,
    loading,
    total,
    error,
    queryParams,
    initialize,
    handlePageChange,
    handleSearch,
    handleReset,
  } = useVendorBiddingData();

  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [searchBiddingNo, setSearchBiddingNo] = useState<string>("");
  const [searchBiddingName, setSearchBiddingName] = useState<string>("");

  // 当弹窗打开时初始化数据
  useEffect(() => {
    if (open) {
      initialize();
      setSelectedRowKeys([]);
      setSearchBiddingNo("");
      setSearchBiddingName("");
    }
  }, [open, initialize]);

  // ==================== 搜索和过滤 ====================

  /**
   * 处理搜索
   */
  const handleSearchClick = useCallback(async () => {
    await handleSearch({
      biddingNo: searchBiddingNo || undefined,
      biddingName: searchBiddingName || undefined,
    });
  }, [searchBiddingNo, searchBiddingName, handleSearch]);

  /**
   * 重置搜索条件
   */
  const handleResetSearch = useCallback(async () => {
    setSearchBiddingNo("");
    setSearchBiddingName("");
    await handleReset();
  }, [handleReset]);

  // ==================== 文件处理 ====================

  /**
   * 确认选择
   */
  const handleConfirmSelect = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t("请先选择至少一个报价单"));
      return;
    }

    const selectedQuotations = quotationList.filter((item) =>
      selectedRowKeys.includes(item.id),
    );
    if (selectedQuotations.length > 0) {
      onSelectQuotation?.(selectedQuotations);
      message.success(t("报价单选择成功"));
      handleClose();
    }
  };

  /**
   * 关闭弹窗
   */
  const handleClose = () => {
    setSelectedRowKeys([]);
    onClose();
  };

  // ==================== 表格配置 ====================

  const columns: ColumnsType<QuotationFileRecord> = useMemo(
    () => [
      {
        title: t("询比单号"),
        dataIndex: "biddingNo",
        key: "biddingNo",
        width: "15%",
      },
      {
        title: t("询比名称"),
        dataIndex: "biddingName",
        key: "biddingName",
        width: "20%",
      },
      {
        title: t("物料名称"),
        dataIndex: "materialNames",
        key: "materialNames",
        width: "20%",
      },
      {
        title: t("截止时间"),
        dataIndex: "deadline",
        key: "deadline",
        width: "15%",
        render: (text: string) => text?.split("T")[0] || "-",
      },
      {
        title: t("询比状态"),
        dataIndex: "biddingStatus",
        key: "biddingStatus",
        width: "12%",
        render: (status: string) => {
          const statusMap: Record<string, string> = {
            IN_PROGRESS: t("询比中"),
            COMPLETED: t("已完成"),
          };
          return statusMap[status] || status;
        },
      },
      {
        title: t("创建人"),
        dataIndex: "createByName",
        key: "createByName",
        width: "12%",
      },
    ],
    [t],
  );

  return (
    <Modal
      title={t("报价单")}
      open={open}
      onCancel={handleClose}
      centered
      width={1200}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          {t("取消")}
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirmSelect}
          disabled={selectedRowKeys.length === 0 || loading}
        >
          {t("确认选择")}
        </Button>,
      ]}
    >
      <Spin spinning={loading} tip={t("加载中...")}>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* 错误提示 */}
          {error && (
            <div
              style={{
                padding: "12px",
                backgroundColor: "#fff2f0",
                border: "1px solid #ffccc7",
                borderRadius: "4px",
                color: "#ff4d4f",
              }}
            >
              {error}
            </div>
          )}

          {/* 搜索区域 */}
          <div
            style={{ paddingBottom: "16px", borderBottom: "1px solid #f0f0f0" }}
          >
            <div
              style={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <label style={{ marginBottom: 0, whiteSpace: "nowrap" }}>
                  {t("询比单号")}：
                </label>
                <Input
                  placeholder={t("请输入")}
                  value={searchBiddingNo}
                  onChange={(e) => setSearchBiddingNo(e.target.value)}
                  allowClear
                  style={{ width: "150px" }}
                />
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <label style={{ marginBottom: 0, whiteSpace: "nowrap" }}>
                  {t("询比名称")}：
                </label>
                <Input
                  placeholder={t("请输入")}
                  value={searchBiddingName}
                  onChange={(e) => setSearchBiddingName(e.target.value)}
                  allowClear
                  style={{ width: "150px" }}
                />
              </div>
              <Space>
                <Button
                  type="primary"
                  onClick={handleSearchClick}
                  loading={loading}
                >
                  {t("搜索")}
                </Button>
                <Button onClick={handleResetSearch} loading={loading}>
                  {t("重置")}
                </Button>
              </Space>
            </div>
          </div>

          {/* 文件列表 */}
          <div>
            {quotationList.length > 0 ? (
              <Table
                columns={columns}
                dataSource={quotationList}
                rowKey="id"
                size="small"
                pagination={{
                  current: queryParams.pageNum,
                  pageSize: queryParams.pageSize,
                  total: total,
                  showTotal: (total) =>
                    `${t("共_条数据").replace("{total}", String(total))}`,
                  onChange: (pageNum, pageSize) =>
                    handlePageChange(pageNum, pageSize),
                }}
                rowSelection={{
                  type: "checkbox",
                  selectedRowKeys: selectedRowKeys,
                  onChange: (selectedKeys) => {
                    setSelectedRowKeys(selectedKeys as number[]);
                  },
                }}
                style={{ maxHeight: "400px", overflow: "auto" }}
                loading={loading}
              />
            ) : (
              <Empty
                description={error ? t("加载失败请重试") : t("暂无报价单")}
                style={{ marginTop: "20px", marginBottom: "20px" }}
              />
            )}
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export default QuotationModal;
